import axios from "axios";
import { matchPath } from "react-router-dom";
import { preProcessedMedicalRecordsData } from "../redux/actions/medicalRecords";
import { toISO, getValidNumber, CREATE_REQUEST_STATUS } from '@demandproai/shared';
import * as pdfjsLib from 'pdfjs-dist';
import { DefaultMultiPlaintiffName, CASE_ROUTE_PATTERNS } from "./constant";
import { toaster } from "./toast";
import { getLocalStorageItem } from "./localStorageUtil";
import { CONCATENATION_POSITION } from "./enum";

export const getDefendantId = (defendant) => {
    return defendant?.defendantId;
}

export const setDefendantId = (defendant, id) => {
    if (defendant == null) {
        return null;
    }
    if (id == null) {
        defendant.defendantId = generateRandomIds();
    }
    else {
        defendant.defendantId = id;
    }
}

export const getDefendantName = (defendant) => {
    if (defendant?.name) {
        return defendant.name;
    }
    else {
        return defendant?.personalInfo?.name;
    }
}

export const getAuthtoken = () => {
    return getLocalStorageItem("authtoken");
};

export const removeSpecialCharacters = (str, charsToRemove = `!"#$%&'()*+,-./:;<=>?@[\\]^_\`{|}~`) => {
    const escapedChars = charsToRemove.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const regex = new RegExp(`[${escapedChars}]`, 'g');
    return str.replace(regex, '');
}

export const separateFilenameAndExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
        return { name: filename, ext: '' };
    }
    const name = filename.slice(0, lastDotIndex);
    const ext = filename.slice(lastDotIndex + 1);
    return { name, ext };
}

export function debounce(func, delay) {
    let timeoutId;
    let lastArgs;
    let lastThis;
    // callers awaiting the trailing invocation; all resolve with the same result
    let pendingResolvers = [];

    // runs the trailing call once, settles every awaiting caller, and returns the result promise
    const invoke = () => {
        timeoutId = null;
        const args = lastArgs;
        const thisArg = lastThis;
        const resolvers = pendingResolvers;
        lastArgs = null;
        lastThis = null;
        pendingResolvers = [];
        // call synchronously so fake-timer tests see the side-effects immediately
        try {
            const resultPromise = Promise.resolve(func.apply(thisArg, args));
            resultPromise.then(
                (result) => resolvers.forEach(({ resolve }) => resolve(result)),
                (error) => resolvers.forEach(({ reject }) => reject(error))
            );
            return resultPromise;
        } catch (error) {
            resolvers.forEach(({ reject }) => reject(error));
            return Promise.reject(error);
        }
    };

    const debounced = function (...args) {
        lastArgs = args;
        lastThis = this;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(invoke, delay);
        // resolves only when the trailing call actually fires, propagating its return value
        return new Promise((resolve, reject) => {
            pendingResolvers.push({ resolve, reject });
        });
    };

    // fires the pending call immediately; returns a promise so callers can await completion
    debounced.flush = function () {
        if (!timeoutId) return Promise.resolve();
        clearTimeout(timeoutId);
        return invoke();
    };

    return debounced;
}


export const getNestedValue = (object, path, defaultValue) => {
    if (object == null) return defaultValue;
    const keys = Array.isArray(path)
        ? path
        : path.replace(/\[(\d+)\]/g, '.$1').split('.');

    let result = object;
    for (const key of keys) {
        if (result == null) return defaultValue;

        result = result[key];
    }

    return result === undefined ? defaultValue : result;
};


export const postMedicalRecords = (injuryData, caseId) => {
    return injuryData?.postAccident?.flatMap(providerLevel => {
        const providerName = providerLevel?.providerName;
        const medicalType = providerLevel?.medicalType || 'All Other Medical Records';

        return providerLevel?.medicalRecords?.map(file => {
            const blob = new Blob([file], { type: file.type });
            const newFile = new File([blob], file.name, { type: file.type });

            return {
                ...(!file?.s3UrlPath && { file: newFile }),
                ...(file?.s3UrlPath && { ...file }),
                metaData: {
                    providername: providerName,
                    medicaltype: medicalType,
                    originalFileName: file.name,
                    typeofrecord: 'postMedicalRecords',
                    ...(file?.s3UrlPath && { caseId }),
                }
            };
        }) || [];
    });
}

export const postMedicalBills = (injuryData) => {
    return injuryData?.postAccident?.flatMap(providerLevel => {
        const providerName = providerLevel?.providerName;
        const medicalType = providerLevel?.medicalType || 'All Other Medical Records';

        return providerLevel?.medicalBills?.map(file => {
            const blob = new Blob([file], { type: file.type });
            const newFile = new File([blob], file.name, { type: file.type });

            return {
                file: newFile,
                metaData: {
                    providername: providerName,
                    medicaltype: medicalType,
                    originalFileName: file.name,
                    typeofrecord: 'postMedicalBills'
                }
            };
        }) || [];
    });
}

export const preMedicalRecords = (injuryData, caseId) => {
    return injuryData?.preAccident?.flatMap(providerLevel => {
        const providerName = providerLevel?.providerName;
        const medicalType = providerLevel?.medicalType || 'All Other Medical Records';

        return providerLevel?.medicalRecords?.map(file => {
            const blob = new Blob([file], { type: file.type });
            const newFile = new File([blob], file.name, { type: file.type });

            return {
                ...(!file?.s3UrlPath && { file: newFile }),
                ...(file?.s3UrlPath && { ...file }),
                metaData: {
                    providername: providerName,
                    medicaltype: medicalType,
                    originalFileName: file.name,
                    typeofrecord: 'preMedicalRecords',
                    ...(file?.s3UrlPath && { caseId }),
                }
            };
        }) || [];
    });
}

export const setCookie = (name, value, days = 7) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/`;
};

export const getCookie = (name) => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.split('=').map(c => c.trim());
        if (cookieName === name) {
            return cookieValue;
        }
    }
    return null;
}

export const deleteCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

export const getLoggedInUserData = () => {
    const token = getAuthtoken()
    const parts = token.split('.');
    const payload = JSON.parse(window.atob(parts[1]));
    return payload;
}

export const generateRandomIds = () => {
    const randomDigits = Math.floor(10000 + Math.random() * 9000000000);
    return String(randomDigits);
}

export const sanitizeCaseFormData = (data, schema) => {
    if (Array.isArray(schema)) {
        return sanitizeArray(data, schema);
    }

    const sanitized = {};
    for (const key in schema) {
        const defaultValue = schema[key];
        const value = data?.[key];

        if (Array.isArray(defaultValue)) {
            sanitized[key] = sanitizeArray(value, defaultValue);
        } else if (typeof defaultValue === "object" && defaultValue !== null && !Array.isArray(defaultValue)) {
            sanitized[key] = sanitizeCaseFormData(value || {}, defaultValue);
        } else {
            sanitized[key] = sanitizePrimitive(value, defaultValue);
        }
    }
    return sanitized;
};

const sanitizePrimitive = (value, originalValue) => {
    const expectedType = typeof originalValue;

    if (expectedType === "string") {
        return value != null ? String(value) : "";
    }

    if (expectedType === "number") {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
    }

    if (expectedType === "boolean") {
        if (typeof value === "string") {
            return value.toLowerCase() === "true" || value === "1";
        }
        return Boolean(value);
    }

    return value != null ? value : originalValue;
};

const sanitizeArray = (formValueArray, schemaArray) => {
    if (!Array.isArray(schemaArray)) return [];

    const schemaObject = schemaArray[0];
    if (typeof schemaObject !== "object") {
        return Array.isArray(formValueArray)
            ? formValueArray.map(val => sanitizePrimitive(val, schemaObject))
            : [];
    }

    if (!Array.isArray(formValueArray)) return [];

    const sanitized = formValueArray.map(item =>
        sanitizeCaseFormData(item, schemaObject)
    );

    if ('text' in schemaObject && typeof schemaObject.text === 'string') {
        const hasNonEmptyText = sanitized.some(obj => obj?.text?.trim());
        return hasNonEmptyText ? sanitized : [];
    }
    return sanitized;
};

export const isPdfPasswordProtect = async (file) => {
    try {
        // Configure PDF.js worker
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
                'pdfjs-dist/build/pdf.worker.min.mjs',
                import.meta.url
            ).toString();
        }

        // Read the file as ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();
        const typedArray = new Uint8Array(arrayBuffer);

        // Try to load the PDF document
        const loadingTask = pdfjsLib.getDocument({
            data: typedArray,
            password: '', // Try with empty password first
            stopAtErrors: false
        });

        try {
            await loadingTask.promise;
            // If we successfully loaded without password, it's not encrypted
            return false;
        } catch (err) {
            // Check if the error is specifically about password/encryption
            if (err.name === 'PasswordException' ||
                err.message?.includes('password') ||
                err.message?.includes('encrypted') ||
                err.message?.includes('No password given')) {
                return true;
            }

            // For other errors
            console.warn('PDF.js error, falling back to header inspection:', err.message);
        }

    } catch (err) {
        console.error('Error checking PDF encryption:', err);
        return false; // Fail safe - assume not encrypted if check fails
    }
};

export const validationFilesFunc = async (files, setFileErrorList) => {

    const errorList = [];
    const validFiles = [];

    for (const item of files) {
        const isProtected = await isPdfPasswordProtect(item);
        if (item.size === 0) {
            errorList.push({
                fileName: item?.name,
                fileSize: item.size
            });
            continue;
        }

        if (item.size > (500 * 1024 * 1024)) {
            errorList.push({
                fileName: item?.name,
                fileSize: item.size,
                isFileLarge: true
            });
            continue;
        }

        if (item.type !== "application/pdf") {
            const fileName = (item?.name || '').toLowerCase();
            const isPdfByName = fileName.endsWith('.pdf');
            if (isPdfByName && !item.type) {
                validFiles.push(item);
                continue;
            }
            errorList.push({
                fileName: item?.name,
                fileTypeError: true
            });
            continue;
        }

        if (isProtected) {
            errorList.push({
                fileName: item?.name,
                isPasswordProtected: true
            });
            continue;
        }

        validFiles.push(item);
    }

    if (errorList?.length) {
        setFileErrorList(errorList);
    }

    return validFiles;
};

export const transformPageCountToPageNumberUIArray = (pageCount) => {
    console.log("Transforming page count ", pageCount, " to UI array");
    if (pageCount <= 6) {
        return Array.from({ length: pageCount }, (_, i) => i + 1);
    }
    else {
        return [1, 2, 3, "...", pageCount - 1, pageCount];
    }
}

export const putFileToS3 = async (preSignedUrl, file, updateCaseFileUploadProgress) => {
    const res = await axios.put(preSignedUrl.signedUrl, file, {
        headers: { 'Content-Type': file?.type },
        onUploadProgress: (event) => {
            updateCaseFileUploadProgress(file, event.loaded, event.total);
        }
    });
    if (res.status === 200) return preSignedUrl;
    throw new Error("Upload failed with status " + res.status)
}

export function createMedicalObject(medicalProviders = []) {
    const result = {
        postAccident: [],
        preAccident: []
    };

    medicalProviders.forEach(elem => {
        const post = elem?.postAccident.medicalRecords?.filter(e => e?.providerName) || [];
        const pre = elem?.preAccident.medicalRecords?.filter(e => e?.providerName) || [];

        result.postAccident.push(post);

        result.preAccident.push(pre);
    });
    return result;
}

export const clearStoredGlobalState = (dispatch) => {
    // Only clear medical record processing state - form data is now in Formik
    dispatch(preProcessedMedicalRecordsData({}));
};

export const generateDefaultProviderName = (fullName, index) => {
    let initials = '';
    if (fullName?.trim()) {
        const parts = fullName.trim().split(' ');
        const firstInitial = parts[0]?.[0] ?? '';
        const lastInitial = parts.length > 1 ? parts.at(-1)[0] : '';
        initials = (firstInitial + lastInitial).toUpperCase();
    }
    const defaultProviderName = `Default Provider ${initials} ${index + 1}`;
    return defaultProviderName;
}

export const prepFileDataForDedicatedUpload = (files) => {
    return files.map(file => ({
        file,
        type: file.type,
        name: file.name,
        metaData: {
            originalFileName: file.name,
        }
    }));
}

export const prepFileMetaDataForDedicatedUpload = (fileObjs, defaultMetadata = {}) => {
    return fileObjs.map(obj => ({
        file: obj.file || obj,
        name: obj.file?.name || obj.name,
        type: obj.file?.type || obj.type,
        metaData: {
            ...defaultMetadata,
            // If the object has specific metadata fields, include them
            ...(obj.defendants !== undefined && { defendants: obj.defendants }),
        }
    }));
}

export const getIsShowCreditInfo = (selectedTiffIndex, parentFormik) => {
    const isEditMode = selectedTiffIndex < parentFormik.values.clients.length;
    const isMultiClientChargeable = parentFormik.values.clients.length >= 1 && !isEditMode && parentFormik.values.clients[0]?.personalInfo?.clientName !== DefaultMultiPlaintiffName.DEFAULT_PLAINTIFF_Name;
    const tokenPayload = getLoggedInUserData();
    const isChargePerPlaintiff = !!tokenPayload?.subscription?.chargeAdditionalPlaintiffs;
    return isMultiClientChargeable && isChargePerPlaintiff;
};

export const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Resolves with the element once it appears, or null once timeoutMs has passed.
// Callers decide what an absent element means rather than getting a rejection
export const waitForElement = ({ selector, timeoutMs, pollMs = 50 }) => {
    const existing = document.querySelector(selector);
    if (existing) {
        return Promise.resolve(existing);
    }

    return new Promise(resolve => {
        const startedAt = Date.now();
        const poll = setInterval(() => {
            const element = document.querySelector(selector);
            const hasTimedOut = Date.now() - startedAt >= timeoutMs;

            if (element || hasTimedOut) {
                clearInterval(poll);
                resolve(element ?? null);
            }
        }, pollMs);
    });
};

export const copyTextToClipboard = (value) => {
    navigator.clipboard.writeText(value)
        .then(() => toaster({ message: `${value} copied to clipboard`, success: true }))
        .catch((err) => console.error("Error copying text: ", err));
};

export const validateAutoAccidentSettlement = ({
    isAutoAccident,
    thirdPartyAmount,
    uimUmAmount,
    isCaseSettled
}) => {
    if (!isAutoAccident) return { isValid: true };

    const hasThirdParty = getValidNumber(thirdPartyAmount) > 0;
    const hasUimUm = getValidNumber(uimUmAmount) > 0;

    if (hasThirdParty && hasUimUm && !isCaseSettled) {
        return {
            isValid: false,
            message:
                'Please select the “Case Settled and Closed” box if this case is completely resolved'
        };
    }

    if (hasThirdParty && !hasUimUm && !isCaseSettled) {
        return {
            isValid: true,
            message:
                'If this case is completely resolved, please select the “Case Settled and Closed” box'
        };
    }

    return { isValid: true };
};

export const filterDuplicateFilesByName = ({
    newFiles = [],
    prevFiles = [],
}) => {
    const existingFileNames = prevFiles.map(f => f?.name);

    const uniqueNewFiles = newFiles.filter(
        file => !existingFileNames.includes(file.name)
    );

    if (!uniqueNewFiles.length) {
        toaster({
            message: 'A file with the same name already exists. Please choose a different name',
            success: false
        });
    }

    return uniqueNewFiles;
};

export const checkIsfileUploading = (fileUploadState) => {
    const { uploadQueue, activeUploads, presignedUrlGenerationCount } = fileUploadState;

    const hasActiveUploads = Object.keys(activeUploads).length > 0;
    const hasQueuedUploads = uploadQueue.length > 0;
    const isGeneratingUrls = presignedUrlGenerationCount > 0;

    return hasActiveUploads || hasQueuedUploads || isGeneratingUrls;
};

export const getDateQueryParams = (selectedRange = null) => {
    if (selectedRange?.startDate && selectedRange?.endDate) {
        return {
            startDate: toISO(selectedRange.startDate),
            endDate: toISO(selectedRange.endDate)
        };
    }
    return {
        days: 30
    };
};

export const getDashboardFilterQueryString = ({selectedRange, selectedTiers, selectedStatus, subscriptionKey, selectedAccountExecutive, selectedCompanies}) => {
    const params = getDateQueryParams(selectedRange);
    
    if (selectedTiers?.length) {
        params.tier = selectedTiers;
    }
    if (selectedStatus?.length) {
        params.status = selectedStatus;
    }
    if (subscriptionKey) {
        params.subscriptionKey = subscriptionKey;
    }
    if (selectedAccountExecutive?.length) {
        params.accountExecutive = selectedAccountExecutive;
    }
    if (selectedCompanies?.length) {
        params.companyIds = selectedCompanies;
    }
    return new URLSearchParams(params).toString();
};

export const transformFailedCasesData = (data) => {
    if (!data?.byDatabase || !Array.isArray(data.byDatabase)) {
        return [];
    }

    return data.byDatabase.map(entry => ({
        companyName: entry.database,
        database: entry.database,
        failedCases: entry.failedCases || [],
        failedCasesCount: entry.failedCases?.length || 0,
        value: entry.count || 0,
    }));
};

export const buildDemandMap = (demandMapArray = []) =>
    demandMapArray.reduce((acc, { state, caseType, value, text }) => {
        acc[`${state}__${caseType}__${value}`] = text;
        return acc;
    }, {});

export const getCaseTypeText = ({ caseType, caseTypeMap, caseTypeMapMT }) => {
    if (caseTypeMapMT && caseTypeMapMT[caseType]?.text) {
        return caseTypeMapMT[caseType].text;
    }
    return caseTypeMap[caseType]?.text || '';
};

export const getLoadingPercentage = (updateTime) => {
    if (!updateTime) return 0;
    const updateDate = new Date(updateTime);
    if (isNaN(updateDate.getTime())) return 0;
    const timeElapsedMs = new Date() - updateDate;
    const timeElapsedMin = timeElapsedMs / 1000 / 60;
    const iLogitParameter = timeElapsedMin / 7 - 3;
    const iLogitExponential = Math.pow(Math.E, iLogitParameter);
    const percentage = iLogitExponential / (1 + iLogitExponential);
    const result = Math.floor(percentage * 100);
    return isNaN(result) ? 0 : result;
};

export const getCaseRouteMatch = (pathname) => {
  return CASE_ROUTE_PATTERNS
    .map((pattern) => matchPath(pattern, pathname))
    .find(Boolean);
};

export const downloadFile = ({ fileName = "", fileUrl }) => {
    return new Promise((resolve) => {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName;  // Empty string will use the server's suggested filename
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
            document.body.removeChild(link);
            resolve()
        }, 200);
    });
}

export const isSettledRequest = (request) =>
  request?.status === CREATE_REQUEST_STATUS.COMPLETED || request?.status === CREATE_REQUEST_STATUS.FAILED;

export const mergeIntoResponse = ({ currentResponse = "", text, position }) => {
    if (!text || currentResponse.includes(text)) return currentResponse;
    return position === CONCATENATION_POSITION.PREPEND
        ? [text, currentResponse].filter(Boolean).join("\n\n")
        : [currentResponse, text].filter(Boolean).join("\n\n");
};
